package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.internal.collections.readOnlyCopy
import kotlin.jvm.JvmStatic

/**
 * 物品堆栈占位元素。
 *
 * 注意：此 `ItemStack` 与 `net.minecraft.world.item.ItemStack` 完全独立，
 * 仅在 UI 树中以字符串 ID + 数量 + 若干 vanilla 展示字段描述要展示的物品；
 * 具体解析交由平台适配层处理。
 */
class ItemStack private constructor() : UIElement<ItemStack>() {

    // ===== 核心数据（backing 字段用 Value 后缀避 ktlint "backing property" 规则）=====
    private var itemIdValue: String = ""
    private var countValue: Int = 1

    /** 耐久比例：-1 表示无耐久（不绘制耐久条），0~1 表示已损耗后的剩余耐久。 */
    private var durabilityValue: Float = -1f

    /** 是否带附魔光效。 */
    private var enchantedValue: Boolean = false

    /** 冷却遮罩比例：0~1，0 不显示。 */
    private var cooldownValue: Float = 0f

    /** 自定义显示名：空字符串走 vanilla 默认名。 */
    private var displayNameValue: String = ""

    /** tooltip lore 行列表，构造后只读。 */
    private var loreValue: List<String> = emptyList()

    /** 透传 NBT，由平台适配层解释。 */
    private var customNbtValue: Map<String, Any> = emptyMap()

    /** customNbt 每次设置后递增，供渲染缓存判断元素内容是否变化。 */
    private var customNbtRevisionValue: Long = 0

    /** customNbt 序列化后的 SNBT 字符串缓存；customNbt 变化时清空，避免每帧重复序列化。 */
    private var cachedSnbtValue: String? = null
    private var cachedSnbtRevision: Long = -1L

    /** 是否显示背景框（默认 true）。 */
    private var showBackgroundValue: Boolean = true

    /** 是否已由用户显式设置 tooltip（用于判断是否自动生成）。 */
    private var tooltipExplicitValue: Boolean = false

    /** 数量徽标 9 宫格锚点（默认 "bottom-right" = vanilla 右下角）。 */
    private var countAnchorValue: String = "bottom-right"

    /** 数量徽标 X 偏移（像素），null = 0 不偏移。 */
    private var countOffsetXValue: Float? = null

    /** 数量徽标 Y 偏移（像素），null = 0 不偏移。 */
    private var countOffsetYValue: Float? = null

    // ===== Task 2a：视觉覆盖字段（可空；null = 回退主题 token）=====
    /** 背景框外层色（ARGB）；null 时回退主题 itemStackBackgroundColor。 */
    private var backgroundColorValue: Int? = null

    /** 背景框内层色（ARGB）；null 时回退主题 itemStackBackgroundInnerColor。 */
    private var backgroundInnerColorValue: Int? = null

    /** hover 高亮叠加色（ARGB）；null 时回退主题 itemStackHoverColor。 */
    private var hoverOverlayColorValue: Int? = null

    /** 背景框外框边框 / 图标内缩宽度（像素）；null 时回退硬编码 1f。 */
    private var backgroundBorderWidthValue: Float? = null

    // ===== CRTP 自引用 =====
    override fun self(): ItemStack = this

    // ===== 核心 fluent setter（保留方法式签名供 Java 调用方）=====

    fun itemId(itemId: String): ItemStack {
        itemIdValue = itemId
        return this
    }

    fun count(count: Int): ItemStack {
        countValue = count
        return this
    }

    fun durability(durability: Float): ItemStack {
        durabilityValue = durability
        return this
    }

    fun enchanted(enchanted: Boolean): ItemStack {
        enchantedValue = enchanted
        return this
    }

    fun cooldown(cooldown: Float): ItemStack {
        cooldownValue = cooldown
        return this
    }

    fun displayName(displayName: String): ItemStack {
        displayNameValue = displayName
        return this
    }

    /** 复制传入列表防止外部修改污染元素状态。 */
    fun lore(lore: List<String>): ItemStack {
        loreValue = if (lore.isEmpty()) emptyList() else ArrayList(lore)
        return this
    }

    /** 对所有嵌套容器建立防御性快照（回植 dc54af899）：外部后续修改不影响元素状态。 */
    fun customNbt(customNbt: Map<String, Any>): ItemStack {
        customNbtValue = snapshotCustomNbt(customNbt)
        customNbtRevisionValue++
        cachedSnbtValue = null
        return this
    }

    fun showBackground(v: Boolean): ItemStack {
        showBackgroundValue = v
        return this
    }

    /** 数量徽标锚点；null/空串回退到 "bottom-right"（vanilla 右下角）。 */
    fun countAnchor(v: String?): ItemStack {
        countAnchorValue = if (v.isNullOrEmpty()) "bottom-right" else v
        return this
    }

    fun countOffsetX(v: Float?): ItemStack {
        countOffsetXValue = v
        return this
    }

    fun countOffsetY(v: Float?): ItemStack {
        countOffsetYValue = v
        return this
    }

    // ===== getter（保留方法式签名供 Java / Kotlin 调用方）=====

    fun itemId(): String = itemIdValue
    fun count(): Int = countValue
    fun durability(): Float = durabilityValue
    fun enchanted(): Boolean = enchantedValue
    fun cooldown(): Float = cooldownValue
    fun displayName(): String = displayNameValue

    /** 返回不可变副本，外部修改不会影响元素状态。 */
    // 只读拷贝（readOnlyCopy 契约）：与迁移前 Collections.unmodifiableList 行为等价
    fun lore(): List<String> = readOnlyCopy(loreValue)

    /** 返回独立深层快照（回植 dc54af899）：外部修改不会影响元素状态。 */
    fun customNbt(): Map<String, Any> = snapshotCustomNbt(customNbtValue)

    /** 返回内部只读快照，仅供运行时缓存/序列化热路径使用（避免每次深层拷贝）。 */
    internal fun customNbtSnapshot(): Map<String, Any> = customNbtValue

    /** 返回 customNbt 当前内部版本，仅供运行时缓存使用。 */
    internal fun customNbtRevision(): Long = customNbtRevisionValue

    /**
     * 返回 customNbt 序列化后的 SNBT 字符串缓存；revision 未变化时直接返回缓存值。
     *
     * 首次调用或 customNbt 变化后 [cachedSnbtValue] 为 null，调用方应通过 [cacheSnbt] 填充。
     */
    internal fun cachedSnbt(): String? {
        if (cachedSnbtRevision == customNbtRevisionValue) return cachedSnbtValue
        return null
    }

    /** 填充 SNBT 缓存并返回；仅供渲染层在 [cachedSnbt] 未命中时调用。 */
    internal fun cacheSnbt(snbt: String?): String? {
        cachedSnbtValue = snbt
        cachedSnbtRevision = customNbtRevisionValue
        return snbt
    }

    fun showBackground(): Boolean = showBackgroundValue
    fun countAnchor(): String = countAnchorValue
    fun countOffsetX(): Float? = countOffsetXValue
    fun countOffsetY(): Float? = countOffsetYValue

    // ===== Task 2a：视觉覆盖 fluent setter / getter ====
    // 方法引用形如 `ItemStack::backgroundColor` 仍由 Java ControlVisualPropContributor 消费；
    // Java 编译器按 getter（0 参）vs setter（1 参）区分重载，兼容无虞。

    fun backgroundColor(v: Int?): ItemStack {
        backgroundColorValue = v
        return this
    }
    fun backgroundColor(): Int? = backgroundColorValue
    fun backgroundInnerColor(v: Int?): ItemStack {
        backgroundInnerColorValue = v
        return this
    }
    fun backgroundInnerColor(): Int? = backgroundInnerColorValue
    fun hoverOverlayColor(v: Int?): ItemStack {
        hoverOverlayColorValue = v
        return this
    }
    fun hoverOverlayColor(): Int? = hoverOverlayColorValue
    fun backgroundBorderWidth(v: Float?): ItemStack {
        backgroundBorderWidthValue = v
        return this
    }
    fun backgroundBorderWidth(): Float? = backgroundBorderWidthValue

    /**
     * 覆盖 tooltip 设置：标记为显式设置，不再自动生成。
     */
    override fun tooltip(tooltip: String?): ItemStack {
        tooltipExplicitValue = true
        super.tooltip(tooltip)
        return this
    }

    /**
     * 获取 tooltip：如果用户未显式设置，自动从 displayName + lore 生成。
     */
    override fun tooltip(): String {
        if (tooltipExplicitValue) {
            return super.tooltip()
        }
        // 自动生成：displayName 为第一行，lore 为后续行
        return buildString {
            if (displayNameValue.isNotEmpty()) {
                append(displayNameValue)
            } else if (itemIdValue.isNotEmpty()) {
                // 回退到 itemId 作为名称
                append(itemIdValue)
            }
            if (loreValue.isNotEmpty()) {
                for (line in loreValue) {
                    if (isNotEmpty()) append('\n')
                    append(line)
                }
            }
        }
    }

    companion object {
        /** 创建新的物品堆栈占位元素。 */
        @JvmStatic
        fun create(): ItemStack = ItemStack()
    }
}

/**
 * customNbt 防御性深层快照（回植 dc54af899，KMP 纯 Kotlin 版）：
 * 对所有嵌套 Map/List/数组（含 typed array）建立独立副本，杜绝外部可变结构污染元素状态。
 * 标量与未知类型原样保留（NBT 值域内均为不可变或按值语义）。
 */
@Suppress("UNCHECKED_CAST")
private fun snapshotCustomNbt(source: Map<String, Any>): Map<String, Any> =
    if (source.isEmpty()) emptyMap() else snapshotNbtMap(source) as Map<String, Any>

private fun snapshotNbtValue(value: Any?): Any? = when (value) {
    is Map<*, *> -> snapshotNbtMap(value)
    is List<*> -> value.map(::snapshotNbtValue)
    is Array<*> -> Array<Any?>(value.size) { snapshotNbtValue(value[it]) }
    is ByteArray -> value.copyOf()
    is IntArray -> value.copyOf()
    is LongArray -> value.copyOf()
    is ShortArray -> value.copyOf()
    is FloatArray -> value.copyOf()
    is DoubleArray -> value.copyOf()
    is BooleanArray -> value.copyOf()
    is CharArray -> value.copyOf()
    else -> value
}

private fun snapshotNbtMap(source: Map<*, *>): Map<Any?, Any?> {
    val snapshot = LinkedHashMap<Any?, Any?>(source.size)
    source.forEach { (key, value) ->
        snapshot[snapshotNbtValue(key)] = snapshotNbtValue(value)
    }
    return snapshot
}
